const AbstractItem = require('@/lib/Items/AbstractItem');
const ItemType = require('@/lib/Items/ItemType');
const MembershipBillingFrequency = require('@/lib/Memberships/MembershipBillingFrequency');
const MembershipDobRequirement = require('@/lib/Memberships/MembershipDobRequirement');
const MembershipValidityPeriodType = require('@/lib/Memberships/MembershipValidityPeriodType');
const moment = require('moment');
const { populate } = require('@/functions/objects');
const { toBool, toDate, toFloat, toInt, toNullableFloat, toNullableInt, toNullableString } = require('@/functions/transform');

class MembershipPlan extends AbstractItem {
    constructor(data = {}) {
        super();

        /**
         * @type {?Date}
         */
        this.allowedUsageFromDate = null;

        /**
         * @type {?Date}
         */
        this.allowedUsageToDate = null;

        this.itemType = ItemType.MEMBERSHIP_PLAN;

        /**
         * @type {number}
         */
        this.people = 0;

        /**
         * @type {number}
         */
        this.minimumAdults = 0;

        /**
         * @type {number}
         */
        this.minimumChildren = 0;

        /**
         * @see {MembershipDobRequirement}
         * @type {number}
         */
        this.captureDatesOfBirth = MembershipDobRequirement.NO;

        /**
         * If minimumAdults or minimumChildren > 0 and this is set this is the maximum age child members can be.
         * If minimumAdults or minimumChildren = 0 and this is set this is the maximum age any member can be.
         *
         * @type {number|null}
         */
        this.memberCutoffAgeYears = null;

        /**
         * @type {boolean}
         */
        this.autoRenew = false;

        /**
         * @type {number}
         */
        this.maxRedemptions = 0;

        /**
         * @see {MembershipValidityPeriodType}
         * @type {?string}
         */
        this.validityPeriodType = null;
        this.validityPeriodCount = null;

        /**
         * @see {MembershipBillingFrequency}
         * @type {?string}
         */
        this.billingFrequency = null;

        /**
         * @type {?number}
         */
        this.installmentAmount = null;

        this.totalPrice = 0.00;

        /**
         * @type {boolean}
         */
        this.membershipsCanBeRedeemedToday = true;

        /**
         * Human-readable description of the payment pattern for the plan.
         * Provided by the API.
         *
         * @type {?string}
         */
        this.paymentDescription = null;

        populate(this, data);
    }

    /**
     * @return {number}
     */
    getID() {
        return this.ID;
    }

    /**
     * @returns {number}
     */
    getPeople() {
        return parseFloat(this.people);
    }

    /**
     * @returns {String}
     */
    getBillingInterval() {
        return (this.billingFrequency === MembershipBillingFrequency.PLAN_LENGTH ? 'At start' : this.billingFrequency);
    }

    /**
     * @returns {Number}
     */
    getTotalPrice() {
        return this.totalPrice;
    }

    /**
     * @returns {object}
     */
    getHydrationMap() {
        return Object.assign(
            super.getHydrationMap(),
            {
                allowedUsageFromDate: toDate,
                allowedUsageToDate: toDate,
                autoRenew: toBool,
                billingFrequency: {},
                captureDatesOfBirth: toInt, /** @see {MembershipDobRequirement} */
                installmentAmount: toNullableFloat,
                maxRedemptions: toInt,
                memberCutoffAgeYears: toNullableInt,
                minimumAdults: toInt,
                minimumChildren: toInt,
                paymentDescription: toNullableString,
                people: toInt,
                totalPrice: toFloat,
                validityPeriodCount: toInt,
                validityPeriodType: {},
            }
        );
    }

    afterHydration(data = {}) {
        this.membershipsCanBeRedeemedToday = this.calculateMembershipsCanBeRedeemedToday();
        super.afterHydration(data);
    }

    getEarliestAllowedStartDate() {
        // Basically this returns the earliest possible start date for the membership plan where the
        // "next" payment due is still in the future. Of course the "next" payment could be an installment
        // or a renewal.
        // If we don't understand the membership plan config (eg because someone has added a new option),
        // we just assume today is the earliest allowed date.
        // Because we need to add 1 day to the date, we actually start with "tomorrow" and do the calculation;
        // if we don't understand the config, we subtract 1 day.
        let result = moment().add(1, 'day');
        switch (this.billingFrequency) {
            case MembershipBillingFrequency.MONTHLY:
                // Earliest allowed date is 1 month ago.
                result = result.subtract(1, 'month');
                break;
            case MembershipBillingFrequency.PLAN_LENGTH:
                // Earliest allowed date is the length of the plan
                switch (this.validityPeriodType) {
                    case MembershipValidityPeriodType.YEAR:
                        result = result.subtract(this.validityPeriodCount, 'year');
                        break;
                    case MembershipValidityPeriodType.MONTH:
                        result = result.subtract(this.validityPeriodCount, 'month');
                        break;
                    default:
                        result = moment().subtract(1, 'day'); // Back to today.
                        break;
                }
                break;
            default:
                result = moment().subtract(1, 'day'); // Back to today.
                break;
        }

        // If the membership has an allowed usage date range, we have to make sure the return value
        // is compatible with that.
        if (this.allowedUsageFromDate != null) {
            let allowedUsageStart = moment(this.allowedUsageFromDate);
            if (result < allowedUsageStart) {
                result = allowedUsageStart;
            }
        }

        return result;
    }

    getLatestAllowedStartDate() {
        // At time of writing, this is null, unless the plan has an allowed usage date range.
        return this.allowedUsageToDate; // If it's null, there's no limit.
    }

    /**
     * Capture dates of birth for every member?
     *
     * @return {boolean}
     */
    alwaysCaptureDob() {
        return this.captureDatesOfBirth === MembershipDobRequirement.MANDATORY;
    }

    /**
     * Capture dates of birth for children only?
     *
     * @return {boolean}
     */
    captureChildDobOnly() {
        return this.captureDatesOfBirth === MembershipDobRequirement.CHILDREN_ONLY;
    }

    /**
     * @return {number}
     */
    getMaxAge() {
        return this.memberCutoffAgeYears;
    }

    /**
     * Determine if the memberCutoffAgeYears applies to every member or only members marked as children.
     * If minimumAdults > 0 OR minimumChildren > 0 the max age only applies to children.
     * If minimumAdults = 0 AND minimumChildren = 0 the max age applies to every member.
     *
     * @return {boolean}
     */
    maxAgeIsForChildOnly() {
        return this.minimumAdults > 0 || this.minimumChildren > 0;
    }

    maxAgeIsForEverybody() {
        return !this.maxAgeIsForChildOnly();
    }

    canBeSoldToday() {
        // Plan can be sold today unless it has an allowed usage date range and the "to" date is in the past.
        return this.allowedUsageToDate == null || moment().isSameOrBefore(this.allowedUsageToDate, 'day');
    }

    /**
     * @private
     * Does the actual calculation used for the this.membershipsCanBeRedeemedToday property.
     *
     * @return {boolean}
     */
    calculateMembershipsCanBeRedeemedToday() {
        // The "isBetween" comparison ignores the time component and is inclusive of the dates.
        return this.allowedUsageFromDate == null
            || moment().isBetween(this.allowedUsageFromDate, this.allowedUsageToDate, 'day', '[]');
    }
}

module.exports = MembershipPlan;
